import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createTreatment,
  getTreatment,
  logTreatmentReadAudit,
  getTreatmentViews,
  viewTreatment,
  getTreatmentsForPatient,
  getTreatmentsForTherapist,
  updateTreatment,
  addTreatmentComment,
  deleteTreatment,
  softDeleteTreatment,
  getPatientTreatmentDraft,
  createTreatmentTag,
  getTreatmentTags,
  updateTreatmentTag,
  deleteTreatmentTag,
  getTreatmentVersions,
} from "../../firebase";

export const treatmentsApi = createApi({
  reducerPath: "treatmentsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Treatments", "Treatment", "TreatmentTags", "TreatmentVersions", "TreatmentViews"],
  endpoints: (builder) => ({
    // ---- Multi-clinic v2 Phase 3: treatment read audit ----
    logTreatmentReadAudit: builder.mutation({
      async queryFn({ clinicId, view, viewer }) {
        const logged = await logTreatmentReadAudit(clinicId, view, viewer); // never throws
        return { data: { logged } };
      },
    }),
    getTreatmentViews: builder.query({
      async queryFn({ clinicId, ...options }) {
        try {
          const result = await getTreatmentViews(clinicId, options);
          return { data: result };
        } catch (error) {
          console.error("[CK-7132] Error fetching treatment views:", {
            errorCode: "CK-7132",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "TreatmentViews", id: "LIST" }],
    }),
    // Get treatments for a patient
    getTreatmentsForPatient: builder.query({
      async queryFn({ clinicId, patientId, therapistId, status }) {
        try {
          const treatments = await getTreatmentsForPatient(clinicId, patientId, {
            therapistId,
            status,
          });
          return { data: treatments };
        } catch (error) {
          console.error("[CK-7100] Error fetching treatments for patient:", {
            errorCode: "CK-7100",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Treatments", id })),
              { type: "Treatments", id: `PATIENT_${patientId}` },
            ]
          : [{ type: "Treatments", id: `PATIENT_${patientId}` }],
    }),

    // Get treatments for a therapist (all patients)
    getTreatmentsForTherapist: builder.query({
      async queryFn({ clinicId, therapistId, patientId, status, startDate, endDate }) {
        try {
          const treatments = await getTreatmentsForTherapist(clinicId, therapistId, {
            patientId,
            status,
            startDate,
            endDate,
          });
          return { data: treatments };
        } catch (error) {
          console.error("[CK-7101] Error fetching treatments for therapist:", {
            errorCode: "CK-7101",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { therapistId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Treatments", id })),
              { type: "Treatments", id: `THERAPIST_${therapistId}` },
            ]
          : [{ type: "Treatments", id: `THERAPIST_${therapistId}` }],
    }),

    // Get a single treatment
    getTreatment: builder.query({
      async queryFn({ clinicId, treatmentId }) {
        try {
          const treatment = await getTreatment(clinicId, treatmentId);
          if (!treatment) {
            return { error: { message: "Treatment not found" } };
          }
          return { data: treatment };
        } catch (error) {
          console.error("[CK-7102] Error fetching treatment:", {
            errorCode: "CK-7102",
            errorMessage: error.message,
            clinicId,
            treatmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { treatmentId }) => [
        { type: "Treatment", id: treatmentId },
      ],
    }),

    // Log treatment view for audit trail (call when user opens treatment page)
    logTreatmentView: builder.mutation({
      async queryFn({ clinicId, treatmentId, therapistId, therapistName }) {
        try {
          const treatment = await viewTreatment(
            clinicId,
            treatmentId,
            therapistId,
            therapistName
          );
          if (!treatment) {
            return { error: { message: "Treatment not found" } };
          }
          return { data: treatment };
        } catch (error) {
          console.error("[CK-7112] Error logging treatment view:", {
            errorCode: "CK-7112",
            errorMessage: error.message,
            clinicId,
            treatmentId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      // No tags to invalidate - this is just for logging
    }),

    // Get patient's treatment draft
    getPatientTreatmentDraft: builder.query({
      async queryFn({ clinicId, patientId, therapistId }) {
        try {
          const draft = await getPatientTreatmentDraft(clinicId, patientId, therapistId);
          return { data: draft };
        } catch (error) {
          console.error("[CK-7103] Error fetching treatment draft:", {
            errorCode: "CK-7103",
            errorMessage: error.message,
            clinicId,
            patientId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: "Treatments", id: `DRAFT_${patientId}` },
      ],
    }),

    // Create a new treatment
    createTreatment: builder.mutation({
      async queryFn({ clinicId, treatmentData, therapistId, therapistName }) {
        try {
          const treatment = await createTreatment(
            clinicId,
            treatmentData,
            therapistId,
            therapistName
          );
          return { data: treatment };
        } catch (error) {
          console.error("[CK-7104] Error creating treatment:", {
            errorCode: "CK-7104",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { treatmentData }) => [
        { type: "Treatments", id: `PATIENT_${treatmentData.patientId}` },
        { type: "Treatments", id: `DRAFT_${treatmentData.patientId}` },
      ],
    }),

    // Update a treatment (draft only)
    updateTreatment: builder.mutation({
      async queryFn({
        clinicId,
        treatmentId,
        treatmentData,
        therapistId,
        therapistName,
      }) {
        try {
          const treatment = await updateTreatment(
            clinicId,
            treatmentId,
            treatmentData,
            therapistId,
            therapistName
          );
          return { data: treatment };
        } catch (error) {
          console.error("[CK-7105] Error updating treatment:", {
            errorCode: "CK-7105",
            errorMessage: error.message,
            clinicId,
            treatmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { treatmentId, treatmentData }) => [
        { type: "Treatment", id: treatmentId },
        { type: "Treatments", id: `PATIENT_${treatmentData?.patientId}` },
        { type: "Treatments", id: `DRAFT_${treatmentData?.patientId}` },
        { type: "TreatmentVersions", id: treatmentId },
      ],
    }),

    // Add a comment to a treatment
    addTreatmentComment: builder.mutation({
      async queryFn({
        clinicId,
        treatmentId,
        content,
        authorId,
        authorName,
        patientId,
      }) {
        try {
          const treatment = await addTreatmentComment(
            clinicId,
            treatmentId,
            content,
            authorId,
            authorName
          );
          return { data: treatment };
        } catch (error) {
          console.error("[CK-7106] Error adding treatment comment:", {
            errorCode: "CK-7106",
            errorMessage: error.message,
            clinicId,
            treatmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { treatmentId }) => [
        { type: "Treatment", id: treatmentId },
      ],
    }),

    // Delete a treatment (draft only)
    deleteTreatment: builder.mutation({
      async queryFn({
        clinicId,
        treatmentId,
        patientId,
        therapistId,
        therapistName,
      }) {
        try {
          await deleteTreatment(clinicId, treatmentId, therapistId, therapistName);
          return { data: treatmentId };
        } catch (error) {
          console.error("[CK-7107] Error deleting treatment:", {
            errorCode: "CK-7107",
            errorMessage: error.message,
            clinicId,
            treatmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Treatments", id: `PATIENT_${patientId}` },
        { type: "Treatments", id: `DRAFT_${patientId}` },
      ],
    }),

    // Soft-delete a LOCKED treatment report (medical record — never hard-deleted).
    // Marks isDeleted and resets the linked appointment to "undocumented".
    softDeleteTreatment: builder.mutation({
      async queryFn({
        clinicId,
        treatmentId,
        therapistId,
        therapistName,
        reason,
      }) {
        try {
          await softDeleteTreatment(
            clinicId,
            treatmentId,
            therapistId,
            therapistName,
            reason
          );
          return { data: treatmentId };
        } catch (error) {
          console.error("[CK-7090] Error soft-deleting treatment:", {
            errorCode: "CK-7090",
            errorMessage: error.message,
            clinicId,
            treatmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { treatmentId, patientId }) => [
        { type: "Treatment", id: treatmentId },
        { type: "Treatments", id: `PATIENT_${patientId}` },
        { type: "TreatmentVersions", id: treatmentId },
      ],
      // The linked appointment's treatmentId / summarized flags were reset in
      // the helper — refresh calendar caches (diary tab, pending actions,
      // "appointments without treatment" picker).
      async onQueryStarted({ appointmentId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({
            type: "calendarApi/invalidateTags",
            payload: [
              ...(appointmentId
                ? [{ type: "Appointment", id: appointmentId }]
                : []),
              { type: "Appointments", id: "LIST" },
              { type: "Appointments", id: "PENDING_ACTION" },
            ],
          });
        } catch (_) {
          // mutation failed — nothing to invalidate
        }
      },
    }),

    // Get version history for a treatment
    getTreatmentVersions: builder.query({
      async queryFn({ clinicId, treatmentId }) {
        try {
          const versions = await getTreatmentVersions(clinicId, treatmentId);
          return { data: versions };
        } catch (error) {
          console.error("[CK-7113] Error fetching treatment versions:", {
            errorCode: "CK-7113",
            errorMessage: error.message,
            clinicId,
            treatmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { treatmentId }) => [
        { type: "TreatmentVersions", id: treatmentId },
      ],
    }),

    // Get treatment tags
    getTreatmentTags: builder.query({
      async queryFn({ clinicId }) {
        try {
          const tags = await getTreatmentTags(clinicId);
          return { data: tags };
        } catch (error) {
          console.error("[CK-7108] Error fetching treatment tags:", {
            errorCode: "CK-7108",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["TreatmentTags"],
    }),

    // Create treatment tag
    createTreatmentTag: builder.mutation({
      async queryFn({ clinicId, tagData }) {
        try {
          const tag = await createTreatmentTag(clinicId, tagData);
          return { data: tag };
        } catch (error) {
          console.error("[CK-7109] Error creating treatment tag:", {
            errorCode: "CK-7109",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["TreatmentTags"],
    }),

    // Update treatment tag
    updateTreatmentTag: builder.mutation({
      async queryFn({ clinicId, tagId, tagData }) {
        try {
          const tag = await updateTreatmentTag(clinicId, tagId, tagData);
          return { data: tag };
        } catch (error) {
          console.error("[CK-7110] Error updating treatment tag:", {
            errorCode: "CK-7110",
            errorMessage: error.message,
            clinicId,
            tagId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["TreatmentTags"],
    }),

    // Delete treatment tag
    deleteTreatmentTag: builder.mutation({
      async queryFn({ clinicId, tagId }) {
        try {
          await deleteTreatmentTag(clinicId, tagId);
          return { data: tagId };
        } catch (error) {
          console.error("[CK-7111] Error deleting treatment tag:", {
            errorCode: "CK-7111",
            errorMessage: error.message,
            clinicId,
            tagId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["TreatmentTags"],
    }),
  }),
});

export const {
  useLogTreatmentReadAuditMutation,
  useGetTreatmentViewsQuery,
  useGetTreatmentsForPatientQuery,
  useGetTreatmentsForTherapistQuery,
  useGetTreatmentQuery,
  useLogTreatmentViewMutation,
  useGetPatientTreatmentDraftQuery,
  useCreateTreatmentMutation,
  useUpdateTreatmentMutation,
  useAddTreatmentCommentMutation,
  useDeleteTreatmentMutation,
  useSoftDeleteTreatmentMutation,
  useGetTreatmentVersionsQuery,
  useGetTreatmentTagsQuery,
  useCreateTreatmentTagMutation,
  useUpdateTreatmentTagMutation,
  useDeleteTreatmentTagMutation,
} = treatmentsApi;

